/**
 * @module Core
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

/* Disabling no-global-variables as we need to use document */
// tslint:disable:no-global-variables

import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { Content } from '@core/aem/models/content.interface';
import { ContentInfo } from '@core/aem/models/content-info.interface';
import { Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { CommonUtilService } from '@support/shared/services/common-util.service';

@Injectable()
export class SeoService {
  private seoMetaData: ContentInfo;
  private renderer: Renderer2;
  private activeTags: string[];
  constructor(
    // tslint:disable-next-line:no-any
    @Inject(DOCUMENT) private document: any,
    private objectService: ObjectService,
    // private logger: LoggerService,
    private meta: Meta,
    private rendererFactory: RendererFactory2,
    private stateStoreUtilsService: StateStoreUtilsService,
    private commonUtilsService: CommonUtilService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.activeTags = [];
  }

  /**
   * To set seodata according to keys recieved from AEM
   * @param aemData - AEM data consisting metadata info
   * @return void
   */
  public setSeoMetadata(aemData: Content): void {
    const language = 'en';
    aemData = this.refebricateSEOData(aemData);
    this.seoMetaData = aemData.metadata[language];
    try {
      this.activeTags.forEach((tagName: string) => {
        this.removeMetaTag(tagName);
      });

      this.activeTags = [];
      for (const metaKey in this.seoMetaData) {
        if (metaKey) {
          switch (metaKey) {
            case 'new-title-tag':
              this.setTitle(metaKey); // preferred document title
              break;
            case 'h1':
              this.addH1Metadata(metaKey); // add h1 detail in header
              break;
            case 'meta-keywords':
              this.addMetaTag(metaKey, 'keywords'); // add keywords metatag
              break;
            case 'meta-description':
              this.addMetaTag(metaKey, 'description'); // add description metatag
              break;
            case 'canonical-url':
              this.addCanonicalUrl(metaKey); // add canonical url and hreflang
              break;
            default:
              this.addOtherMetaTags(metaKey); // add remaining metatags
              break;
          }
        }
      }
      /* robot tags add/remove have multiple scenario so handling differently */
      this.handleRobotTag();
    } catch (error) {
      // this.logger.error('Core Module', 'SeoService', 'Something went wrong while setting metadata', 'NA',
      //   error);
    }
  }

  /**
   * To set document title recieved from AEM
   * @param metaKey - key which is holding value of metatag
   * @return void
   */
  private setTitle(metaKey: string): void {
    const title: string = this.objectService.get(this.seoMetaData, metaKey);
    this.document.title = this.refabricateTitle(title);
  }

  /**
   * To set/update H1 tag in head recieved from AEM
   * @param metaKey - key which is holding value of metatag
   * @return void
   */
  private addH1Metadata(metaKey: string): void {
    if (this.objectService.get(this.seoMetaData, metaKey)) {
      let h1Tag = this.document.head.getElementsByTagName('h1');
      if (h1Tag.length > 0) {
        h1Tag[0].innerHTML = this.seoMetaData.h1;
      } else {
        h1Tag = this.renderer.createElement('h1');
        h1Tag.innerHTML = this.seoMetaData.h1;
        this.renderer.appendChild(this.document.head, h1Tag);
      }
    }
  }

  /**
   * common method to set metatag with name and content
   * @param metaKey - key which is holding value of metatag
   * @param metaName - commonly name attribute of metatag
   * @return void
   */
  private addMetaTag(metaKey: string, metaName: string): void {
    const metaTag = this.meta.getTag('name=' + metaName);
    if (this.objectService.get(this.seoMetaData, metaKey)) {
      this.activeTags.push(metaName);
      if (metaTag) {
        this.meta.updateTag({ name: metaName, content: this.seoMetaData[metaKey] });
      } else {
        this.meta.addTag({
          name: metaName,
          content: this.seoMetaData[metaKey] ? this.seoMetaData[metaKey] : '',
        });
      }
    } else if (metaTag) {
      this.removeMetaTag(metaName);
    }
  }

  private removeMetaTag(metaName: string): void {
    this.meta.removeTag('name=' + metaName);
  }

  /**
   * method to add canonical URL in link tag, recived from AEM
   * @param metaKey - key which is holding value of metatag
   * @return void
   */
  private addCanonicalUrl(metaKey: string): void {
    if (this.objectService.get(this.seoMetaData, metaKey)) {
      const canonicalUrl: string = this.seoMetaData[metaKey];
      this.updateCanonicalStructure(canonicalUrl);
    }
  }

  /**
   * method to set hreflang, href and ref, recived from AEM
   * @param metaKey - key which is holding value of metatag
   * @return void
   */
  private setHrefLang(canonicalUrl: string): void {
    let esHostName: string = '';
    let enHostName: string = '';
    const hostName: string = this.document.location.host;
    const protocolName: string = this.document.location.protocol;
    // if sub domain is 'es' then we need to replace it for en tag
    if (this.document.location.origin.indexOf('//es.') > 0) {
      esHostName = this.document.location.origin;
      enHostName = this.document.location.origin.replace('es.', '');
    } else {
      esHostName = protocolName + '//es.' + hostName;
      enHostName = this.document.location.origin;
    }
    let hrefLangTag = this.document.querySelector('link[hreflang="es" ]');
    if (hrefLangTag) {
      hrefLangTag.setAttribute('href', esHostName + canonicalUrl);
    } else {
      hrefLangTag = this.renderer.createElement('link');
      hrefLangTag.setAttribute('ref', 'alternate');
      hrefLangTag.setAttribute('hreflang', 'es');
      hrefLangTag.setAttribute('href', canonicalUrl ? esHostName + canonicalUrl : '');
      this.renderer.appendChild(this.document.head, hrefLangTag);
    }
    hrefLangTag = this.document.querySelector('link[hreflang="en" ]');
    if (hrefLangTag) {
      hrefLangTag.setAttribute('href', enHostName + canonicalUrl);
    } else {
      hrefLangTag = this.renderer.createElement('link');
      hrefLangTag.setAttribute('ref', 'alternate');
      hrefLangTag.setAttribute('hreflang', 'en');
      hrefLangTag.setAttribute('href', canonicalUrl ? enHostName + canonicalUrl : '');
      this.renderer.appendChild(this.document.head, hrefLangTag);
    }
    // adding hreflang x-default tag addition.
    hrefLangTag = this.document.querySelector('link[hreflang="x-default" ]');
    if (hrefLangTag) {
      hrefLangTag.setAttribute('href', enHostName + canonicalUrl);
    } else {
      hrefLangTag = this.renderer.createElement('link');
      hrefLangTag.setAttribute('ref', 'alternate');
      hrefLangTag.setAttribute('hreflang', 'x-default');
      hrefLangTag.setAttribute('href', canonicalUrl ? enHostName + canonicalUrl : '');
      this.renderer.appendChild(this.document.head, hrefLangTag);
    }
  }

  /**
   * method to handle various conditions for adding robot tags
   * @return void
   */
  private handleRobotTag(): void {
    let noIndex: boolean = false;
    let noFollow: boolean = false;
    if (this.objectService.get(this.seoMetaData, 'no-index')) {
      noIndex = this.objectService.get(this.seoMetaData, 'no-index') === 'true';
    }
    if (this.objectService.get(this.seoMetaData, 'no-follow')) {
      noFollow = this.objectService.get(this.seoMetaData, 'no-follow') === 'true';
    }
    const robotMetaTag = this.meta.getTag('name=robots');
    if (robotMetaTag) {
      this.meta.removeTag('name=robots');
    }
    this.addRobotTag(noIndex, noFollow);
  }

  /**
   * method to add robot tags
   * @param noIndex - AEM value of noIndex
   * @param noFollow - AEM value of noFollow
   * @param robotMetaTag - metatag consisting robots as name
   * @return void
   */
  private addRobotTag(noIndex: boolean, noFollow: boolean): void {
    const contentValue =
      (noIndex && noFollow && 'noIndex,noFollow') ||
      (noIndex && 'noindex') ||
      (noFollow && 'nofollow');
    if (contentValue) {
      this.meta.addTag({ name: 'robots', content: contentValue });
    }
  }

  /**
   * method to add remmaining meta tags
   * @param metaKey - key which is holding value of metatag
   * @return void
   */
  private addOtherMetaTags(metaKey: string): void {
    const isRobotTag = metaKey === 'no-index' || metaKey === 'no-follow';
    if (!isRobotTag) {
      this.addMetaTag(metaKey, metaKey);
    }
  }

  private refabricateTitle(title: string): string {
    const defaultTitle = 'T-Mobile Prepaid';
    const oldTitle = 'T-Mobile';
    if (title) {
      return title.split('|').length > 1
        ? this.handleConcat(title, defaultTitle, oldTitle)
        : title.endsWith(oldTitle)
          ? defaultTitle
          : [title, defaultTitle].join(' | ');
    }
    return defaultTitle;
  }

  private handleConcat(title: string, defaultTitle: string, oldTitle: string): string {
    const titleArr = title.split('|').map((item: string) => item.trim());
    if (
      titleArr[titleArr.length - 1] === oldTitle ||
      titleArr[titleArr.length - 1] === defaultTitle
    ) {
      titleArr[titleArr.length - 1] = defaultTitle;
    } else {
      titleArr.push(defaultTitle);
    }
    return titleArr.join(' | ');
  }

  private refebricateSEOData(data: Content): Content {
    // const canonicalUrl = this.objectService.get(data, 'metadata.en.canonical-url', '');
    const [basePath, productParam, deviceName, color, memory]: string[] =
      this.deStructureCurrentRoute();
    if (basePath && basePath === SHARED_CONSTANTS.PRODUCTDETAIL) {
      // const [brandName, model, ...params]: string[] = productParam.split('-'); TODO: incase individual handling is needed
      data.metadata.en['canonical-url'] = `/${basePath}/${productParam}`;
      data.metadata.en['new-title-tag'] = this.commonUtilsService.messageFormatter(
        this.generatePlaceHolder(data, 'new-title-tag'),
        [deviceName]
      );
      data.metadata.en['meta-description'] = this.commonUtilsService.messageFormatter(
        this.generatePlaceHolder(data, 'meta-description', SHARED_CONSTANTS.DESC_PLACEHOLDER),
        [deviceName]
      );
    }
    return data;
  }

  private standarizeString(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  private getValue(base: string, source: string): string {
    const sourceObj = JSON.parse(source);
    return base && this.objectService.get(sourceObj, `${base}`)
      ? this.objectService.get(sourceObj, `${base}`)
      : this.standarizeString(base);
  }

  public setSEOModifier(data: Content, canonicalURL: string): void {
    if (
      canonicalURL === SHARED_CONSTANTS.DEVICEDETAILURL &&
      data &&
      data.model &&
      data.model['model-data'] &&
      data.model['model-data']['module-enhancements']
    ) {
      const modifier = this.objectService.get(
        data,
        'model.model-data.module-enhancements.0.seoModifiers',
        ''
      );
      this.stateStoreUtilsService.setSEOModifiers(modifier);
    }
  }

  private generatePlaceHolder(data: Content, metaKey: string, placeholder?: string): string {
    return data.metadata.en[metaKey]
      ? data.metadata.en[metaKey]
      : placeholder
        ? placeholder
        : SHARED_CONSTANTS.DEFAULT_PLACEHOLDER;
  }

  public publishMetaBeforeContent(): void {
    const [basePath, productParam, deviceName, color, memory]: string[] =
      this.deStructureCurrentRoute();
    const canonicalBase = `/${basePath}/${productParam}`;
    let titleValue = this.commonUtilsService.messageFormatter(
      SHARED_CONSTANTS.DEFAULT_PLACEHOLDER,
      [deviceName]
    );
    titleValue = this.refabricateTitle(titleValue);
    const descValue = this.commonUtilsService.messageFormatter(SHARED_CONSTANTS.DESC_PLACEHOLDER, [
      deviceName,
    ]);
    // publish metaData
    this.document.title = titleValue;
    this.updateCanonicalStructure(canonicalBase);
    this.publishMetaData('description', descValue);
  }

  private deStructureCurrentRoute(): string[] {
    let url = this.stateStoreUtilsService.getCurrentUrl()
      ? this.stateStoreUtilsService.getCurrentUrl()
      : window.location.pathname;
    let deviceName = '';
    url = url.startsWith('/') ? url.slice(1, url.length) : url;
    const [basePath, productParam, color, memory]: string[] = url.split('/');
    const seoModifiers = this.stateStoreUtilsService.getSEOModifiers();
    if (productParam) {
      deviceName = productParam
        .split('-')
        .map((value: string) => this.getValue(value, seoModifiers))
        .join(' ');
    }
    return [basePath, productParam, deviceName, color, memory];
  }

  private publishMetaData(name: string, value: string): void {
    const metaTag = this.meta.getTag('name=' + name);
    this.activeTags.push(name);
    if (metaTag) {
      this.meta.updateTag({ name: name, content: value });
    } else {
      this.meta.addTag({ name: name, content: value });
    }
  }

  private updateCanonicalStructure(canonicalUrl: string): void {
    const urlOrigin: string = this.document.location.origin;
    let linkTag = this.document.querySelector('link[rel="canonical"]');
    if (linkTag) {
      linkTag.setAttribute('href', urlOrigin + canonicalUrl);
      this.setHrefLang(canonicalUrl);
    } else {
      linkTag = this.renderer.createElement('link');
      linkTag.setAttribute('rel', 'canonical');
      linkTag.setAttribute('href', canonicalUrl ? urlOrigin + canonicalUrl : '');
      this.renderer.appendChild(this.document.head, linkTag);
      this.setHrefLang(canonicalUrl);
    }
  }
}
